import Component from '@glimmer/component'
import type { TOC } from '@ember/component/template-only'
import { queryParam } from 'teacher-ui-client/decorators/query-param'
import type { ReportData } from 'teacher-ui-client/components/reporting/writing-legends/quizzes-completed/history/report-loader'
import {
  serialize as serializeSort,
  deserialize as deserializeSort,
  updateSortColumns,
} from '@blakeelearning/data-tables/utils/sorting'
import { action } from '@ember/object'
import type { SortingConfig } from '@blakeelearning/data-tables/utils/sorting'
import { getSortViewModel } from 'teacher-ui-client/utils/sorting'
import t from 'ember-intl/helpers/t'
import { on } from '@ember/modifier'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'
import formatValue from 'teacher-ui-client/helpers/format-value'
import joinList from 'teacher-ui-client/helpers/join-list'
import gradeName from 'teacher-ui-client/helpers/grade-name'

interface Signature {
  Element: HTMLTableElement
  Args: {
    isReloading?: boolean
    studentsReportData: ReportData['students']
    isSubco?: boolean
  }
}

export const HistoryTable: TOC<Signature> = <template>
  <table class="table-print-zoom-1/2 divide-dusty-black-200 relative table-fixed divide-y" ...attributes>
    <thead>
      <tr class="bg-white">
        <ThSort @sortKey="first_name" @text={{t "reporting.firstName"}} class="min-w-32" />
        <ThSort @sortKey="last_name" @text={{t "reporting.lastName"}} class="min-w-32" />
        {{#if @isSubco}}
          <ThSort @sortKey="teacher_names" @text={{t "reporting.teacher"}} class="min-w-32" />
          <ThSort @sortKey="grade_position" @text={{t "grade.label"}} class="w-24" />
        {{/if}}
        <ThSort @sortKey="quizzes_taken" @text={{t "reporting.charts.quizScores.quizzesTaken" htmlSafe=true}} />
        <ThSort @sortKey="total_attempts" @text={{t "reporting.charts.quizScores.totalAttempts" htmlSafe=true}} />
        <ThSort @sortKey="avg_score" @text={{t "reporting.charts.quizScores.avgScorePerQuiz" htmlSafe=true}} />
      </tr>
    </thead>
    <tbody class="divide-dusty-black-200 divide-y {{if @isReloading 'opacity-50'}}">
      {{#each @studentsReportData as |student index|}}
        <tr class="bg-white" data-test-table-row="{{index}}">
          <td class="truncate p-3" data-test-cell="first_name">
            {{student.first_name}}
          </td>
          <td class="truncate p-3" data-test-cell="last_name">
            {{student.last_name}}
          </td>
          {{#if @isSubco}}
            <td class="truncate p-3" data-test-cell="teacher_names">
              {{joinList student.teacher_names ", "}}
            </td>
            <td class="p-3" data-test-cell="grade_position">
              {{gradeName student.grade_position}}
            </td>
          {{/if}}
          <td class="p-3" data-test-cell="quizzes_taken">
            {{formatValue student.quizzes_taken}}
          </td>
          <td class="p-3" data-test-cell="total_attempts">
            {{formatValue student.total_attempts}}
          </td>
          <td class="p-3" data-test-cell="avg_score">
            {{formatValue student.avg_score append="%"}}
          </td>
        </tr>
      {{/each}}
    </tbody>
  </table>
</template>

interface ThSortSignature {
  Element: HTMLTableCellElement
  Args: {
    text?: string
    sortKey: string
  }
}

class ThSort extends Component<ThSortSignature> {
  @queryParam('reporting/ui-scope/writing-legends/quizzes-completed') sort!: string

  get sortingConfig() {
    return deserializeSort(this.sort)
  }

  set sortingConfig(newSortConfig: SortingConfig) {
    this.sort = serializeSort(newSortConfig)
  }

  @action
  updateSort() {
    this.sortingConfig = updateSortColumns(this.sortingConfig, this.args.sortKey)
  }

  get sortViewModel() {
    return getSortViewModel(this.sortingConfig, this.args.sortKey)
  }

  <template>
    <th
      class="h-[80px] select-none"
      data-test-sort-key={{@sortKey}}
      aria-sort={{this.sortViewModel.ariaDirection}}
      ...attributes
    >
      <button
        type="button"
        class="flex h-full w-full items-center gap-x-3 p-3 text-left {{if @text 'justify-between' 'justify-end'}}"
        {{on "click" this.updateSort}}
      >
        {{#if @text}}<span class="text-dusty-black-250 font-normal uppercase">{{@text}}</span>{{/if}}
        <PhosphorIcon @icon={{this.sortViewModel.icon}} class="text-dusty-black-400 print:hidden" />
      </button>
    </th>
  </template>
}
